import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Card,
  CardMedia,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  MenuItem,
  Snackbar,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit,
  Save,
  Cancel,
  PhotoCamera,
  Videocam,
  Description,
  Mic,
  AccessTime,
  Delete as DeleteIcon,
  Add as AddIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { API_BASE } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateBr } from '../utils/datetime';

// Limites de upload
const MAX_FOTOS = 10;
const MAX_VIDEOS = 3;
const MAX_TAMANHO_FOTOS_MB = 20;
const MAX_TAMANHO_VIDEOS_MB = 50;

export default function RelatorioDetalhesModal({
  open,
  onClose,
  relatorioId,
  clientes = [],
  startEdit = false,
}) {
  const { token, hasPermission } = useAuth();
  const [relatorio, setRelatorio] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Estados para edição de listas
  const [editMaoDeObra, setEditMaoDeObra] = useState([]); // [{nome, funcionarios}]
  const [editEquipamentos, setEditEquipamentos] = useState([]); // [string]
  const [editAtividades, setEditAtividades] = useState([]); // [string]

  // Estados para upload de mídia
  const [novasFotos, setNovasFotos] = useState([]);
  const [novosVideos, setNovosVideos] = useState([]);
  const [editFotosDescricoes, setEditFotosDescricoes] = useState([]);
  const [editVideosDescricoes, setEditVideosDescricoes] = useState([]);
  const [editDocsDescricoes, setEditDocsDescricoes] = useState([]);
  const [historicoMidias, setHistoricoMidias] = useState([]);
  const [showHistorico, setShowHistorico] = useState(false);
  const fotoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const canUpdate = hasPermission('/relatorio-obras', 'update');

  // Buscar histórico de mídias
  const fetchHistoricoMidias = useCallback(async () => {
    if (!relatorioId || !token) return;
    try {
      const res = await fetch(`${API_BASE}/relatorios_obras/${relatorioId}/historico_midias`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setHistoricoMidias(data);
      }
    } catch (e) {
      console.error('Erro ao carregar histórico de mídias:', e);
    }
  }, [relatorioId, token]);

  const fetchRelatorio = useCallback(async () => {
    if (!relatorioId) return;

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/relatorios_obras/${relatorioId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!res.ok) {
        throw new Error(`Erro ao carregar relatório (HTTP ${res.status})`);
      }

      const data = await res.json();
      setRelatorio(data);
      setEditData({
        cliente_id: data.cliente_id || '',
        data_relatorio: data.data_relatorio || '',
        tempo: data.tempo || '',
        condicao: data.condicao || '',
        contratada: data.contratada || '',
        contrato_numero: data.contrato_numero || '',
        descricao_servico: data.descricao_servico || '',
        transcricao_audio: data.transcricao_audio || '',
      });
      // Inicializar estados de listas
      const parseLista = (val) => {
        if (!val) return [];
        if (Array.isArray(val)) return val;
        try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; }
      };
      const mdo = parseLista(data.mao_de_obra);
      setEditMaoDeObra(mdo.map((i) => typeof i === 'object' ? { nome: i.nome || '', funcionarios: i.funcionarios || 1 } : { nome: String(i), funcionarios: 1 }));
      setEditEquipamentos(parseLista(data.equipamentos).map((i) => typeof i === 'object' ? (i.nome || i.descricao || '') : String(i)));
      setEditAtividades(parseLista(data.atividades).map((i) => typeof i === 'object' ? (i.nome || i.descricao || '') : String(i)));
      setEditFotosDescricoes(parseLista(data.fotos_descricoes).map((d) => String(d || '').slice(0, 100)));
      setEditVideosDescricoes(parseLista(data.videos_descricoes).map((d) => String(d || '').slice(0, 100)));
      setEditDocsDescricoes(parseLista(data.documentos_descricoes).map((d) => String(d || '').slice(0, 100)));
      // Limpar novas mídias ao carregar
      setNovasFotos([]);
      setNovosVideos([]);
    } catch (e) {
      console.error('Erro ao carregar detalhes do relatório:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [relatorioId, token]);

  useEffect(() => {
    if (open) {
      fetchRelatorio();
      fetchHistoricoMidias();
      setEditMode(!!startEdit);
      setShowHistorico(false);
    }
  }, [open, fetchRelatorio, fetchHistoricoMidias, startEdit]);

  // Calcular tamanho total das mídias
  const calcularTamanhoMB = (files) => {
    return files.reduce((acc, f) => acc + (f.file?.size || 0), 0) / (1024 * 1024);
  };

  // Validar limites de upload
  const validarLimites = () => {
    if (novasFotos.length > MAX_FOTOS) {
      return `Máximo de ${MAX_FOTOS} fotos permitidas`;
    }
    if (novosVideos.length > MAX_VIDEOS) {
      return `Máximo de ${MAX_VIDEOS} vídeos permitidos`;
    }
    const tamanhoFotos = calcularTamanhoMB(novasFotos);
    if (tamanhoFotos > MAX_TAMANHO_FOTOS_MB) {
      return `Tamanho total das fotos (${tamanhoFotos.toFixed(2)}MB) excede o limite de ${MAX_TAMANHO_FOTOS_MB}MB`;
    }
    const tamanhoVideos = calcularTamanhoMB(novosVideos);
    if (tamanhoVideos > MAX_TAMANHO_VIDEOS_MB) {
      return `Tamanho total dos vídeos (${tamanhoVideos.toFixed(2)}MB) excede o limite de ${MAX_TAMANHO_VIDEOS_MB}MB`;
    }
    return null;
  };

  // Handlers para adicionar fotos
  const handleAddFotos = (e) => {
    const files = Array.from(e.target.files || []);
    const newFotos = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
      descricao: '',
    }));
    const total = novasFotos.length + newFotos.length;
    if (total > MAX_FOTOS) {
      setSnackbar({
        open: true,
        message: `Máximo de ${MAX_FOTOS} fotos. Você selecionou ${total}.`,
        severity: 'error',
      });
      return;
    }
    setNovasFotos((prev) => [...prev, ...newFotos]);
    e.target.value = '';
  };

  // Handlers para adicionar vídeos
  const handleAddVideos = (e) => {
    const files = Array.from(e.target.files || []);
    const newVideos = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
      descricao: '',
    }));
    const total = novosVideos.length + newVideos.length;
    if (total > MAX_VIDEOS) {
      setSnackbar({
        open: true,
        message: `Máximo de ${MAX_VIDEOS} vídeos. Você selecionou ${total}.`,
        severity: 'error',
      });
      return;
    }
    setNovosVideos((prev) => [...prev, ...newVideos]);
    e.target.value = '';
  };

  // Remover foto nova
  const handleRemoveFoto = (index) => {
    setNovasFotos((prev) => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  // Remover vídeo novo
  const handleRemoveVideo = (index) => {
    setNovosVideos((prev) => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleEditToggle = () => {
    if (editMode) {
      // Cancelar edição - resetar dados e limpar mídias
      setEditData({
        cliente_id: relatorio?.cliente_id || '',
        data_relatorio: relatorio?.data_relatorio || '',
        tempo: relatorio?.tempo || '',
        condicao: relatorio?.condicao || '',
        contratada: relatorio?.contratada || '',
        contrato_numero: relatorio?.contrato_numero || '',
        descricao_servico: relatorio?.descricao_servico || '',
        transcricao_audio: relatorio?.transcricao_audio || '',
      });
      // Resetar listas
      const parseLista = (val) => { if (!val) return []; if (Array.isArray(val)) return val; try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; } };
      const mdo = parseLista(relatorio?.mao_de_obra);
      setEditMaoDeObra(mdo.map((i) => typeof i === 'object' ? { nome: i.nome || '', funcionarios: i.funcionarios || 1 } : { nome: String(i), funcionarios: 1 }));
      setEditEquipamentos(parseLista(relatorio?.equipamentos).map((i) => typeof i === 'object' ? (i.nome || i.descricao || '') : String(i)));
      setEditAtividades(parseLista(relatorio?.atividades).map((i) => typeof i === 'object' ? (i.nome || i.descricao || '') : String(i)));
      setEditFotosDescricoes(parseLista(relatorio?.fotos_descricoes).map((d) => String(d || '').slice(0, 100)));
      setEditVideosDescricoes(parseLista(relatorio?.videos_descricoes).map((d) => String(d || '').slice(0, 100)));
      setEditDocsDescricoes(parseLista(relatorio?.documentos_descricoes).map((d) => String(d || '').slice(0, 100)));
      // Limpar mídias novas
      novasFotos.forEach((f) => URL.revokeObjectURL(f.preview));
      novosVideos.forEach((v) => URL.revokeObjectURL(v.preview));
      setNovasFotos([]);
      setNovosVideos([]);
    }
    setEditMode(!editMode);
  };

  const handleSave = async () => {
    // Validar limites antes de salvar
    const erroLimite = validarLimites();
    if (erroLimite) {
      setSnackbar({ open: true, message: erroLimite, severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      // Serializar listas editadas
      const dataToSave = {
        ...editData,
        mao_de_obra: JSON.stringify(editMaoDeObra.filter((i) => i.nome.trim())),
        equipamentos: JSON.stringify(editEquipamentos.filter((i) => i.trim())),
        atividades: JSON.stringify(editAtividades.filter((i) => i.trim())),
        fotos_descricoes: JSON.stringify(
          (novasFotos.length > 0 ? novasFotos.map((f) => f.descricao || '') : editFotosDescricoes)
            .map((d) => String(d).slice(0, 100))
        ),
        videos_descricoes: JSON.stringify(
          (novosVideos.length > 0
            ? novosVideos.map((v) => v.descricao || '')
            : editVideosDescricoes
          ).map((d) => String(d).slice(0, 100))
        ),
        documentos_descricoes: JSON.stringify(
          (editDocsDescricoes || []).map((d) => String(d || '').slice(0, 100))
        ),
      };

      // Se houver novas mídias, usar FormData
      if (novasFotos.length > 0 || novosVideos.length > 0) {
        const formData = new FormData();

        // Adicionar dados de edição
        Object.entries(dataToSave).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            formData.append(key, value);
          }
        });

        // Adicionar fotos
        novasFotos.forEach((foto) => {
          formData.append('fotos[]', foto.file);
        });

        // Adicionar vídeos
        novosVideos.forEach((video) => {
          formData.append('videos[]', video.file);
        });

        const res = await fetch(`${API_BASE}/relatorios_obras/${relatorioId}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.error || `Erro ao salvar (HTTP ${res.status})`);
        }
      } else {
        // Sem mídias, enviar JSON simples
        const res = await fetch(`${API_BASE}/relatorios_obras/${relatorioId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(dataToSave),
        });

        if (!res.ok) {
          throw new Error(`Erro ao salvar (HTTP ${res.status})`);
        }
      }

      setSnackbar({
        open: true,
        message: 'Relatório atualizado com sucesso!',
        severity: 'success',
      });
      setEditMode(false);
      // Limpar mídias
      novasFotos.forEach((f) => URL.revokeObjectURL(f.preview));
      novosVideos.forEach((v) => URL.revokeObjectURL(v.preview));
      setNovasFotos([]);
      setNovosVideos([]);
      fetchRelatorio(); // Recarrega dados
      fetchHistoricoMidias(); // Recarrega histórico
    } catch (e) {
      setSnackbar({ open: true, message: 'Erro ao salvar: ' + e.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  // Helper para obter nome do cliente
  const getClienteNome = (clienteId) => {
    if (!clienteId) return 'N/A';
    const cliente = clientes.find((c) => c.id === clienteId || c.id === Number(clienteId));
    return cliente?.nome || relatorio?.cliente?.nome || `Cliente ID: ${clienteId}`;
  };

  // Formatar data/hora de criação
  const formatCreatedAt = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  // Parse JSON arrays
  const parseArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };
  const maoDeObra = parseArray(relatorio?.mao_de_obra);
  const equipamentos = parseArray(relatorio?.equipamentos);
  const atividades = parseArray(relatorio?.atividades);

  // URLs de mídia
  const fotosUrls = relatorio?.fotos_urls || [];
  const videosUrls = relatorio?.videos_urls || [];
  const docsUrls = relatorio?.documentos_urls || [];
  const fotosDescricoes = parseArray(relatorio?.fotos_descricoes);
  const videosDescricoes = parseArray(relatorio?.videos_descricoes);
  const docsDescricoes = parseArray(relatorio?.documentos_descricoes);
  const audioUrl = relatorio?.audio_url || null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6">
            Detalhes do Relatório (RDO #{relatorioId})
            {editMode && (
              <Typography component="span" color="secondary" sx={{ ml: 1 }}>
                (Editando)
              </Typography>
            )}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh' }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {relatorio && (
            <Box>
              {/* Informações Básicas */}
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Data
                  </Typography>
                  {editMode ? (
                    <TextField
                      type="date"
                      value={editData.data_relatorio || ''}
                      onChange={(e) => handleChange('data_relatorio', e.target.value)}
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  ) : (
                    <Typography variant="body1">
                      {formatDateBr(relatorio.data_relatorio)}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Período
                  </Typography>
                  {editMode ? (
                    <TextField
                      select
                      value={editData.tempo || ''}
                      onChange={(e) => handleChange('tempo', e.target.value)}
                      size="small"
                      fullWidth
                    >
                      <MenuItem value="manha">Manhã</MenuItem>
                      <MenuItem value="tarde">Tarde</MenuItem>
                      <MenuItem value="noite">Noite</MenuItem>
                    </TextField>
                  ) : (
                    <Typography variant="body1">
                      {relatorio.tempo === 'manha'
                        ? 'Manhã'
                        : relatorio.tempo === 'tarde'
                          ? 'Tarde'
                          : relatorio.tempo === 'noite'
                            ? 'Noite'
                            : relatorio.tempo || 'N/A'}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Cliente
                  </Typography>
                  {editMode ? (
                    <TextField
                      select
                      value={editData.cliente_id || ''}
                      onChange={(e) => handleChange('cliente_id', e.target.value)}
                      size="small"
                      fullWidth
                    >
                      {clientes.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.nome}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <Typography variant="body1" fontWeight="bold">
                      {getClienteNome(relatorio.cliente_id)}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    <AccessTime sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                    Enviado em
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCreatedAt(relatorio.created_at)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Condição climática
                  </Typography>
                  {editMode ? (
                    <TextField
                      value={editData.condicao || ''}
                      onChange={(e) => handleChange('condicao', e.target.value)}
                      size="small"
                      fullWidth
                      placeholder="Ex: Praticável, Chuva..."
                    />
                  ) : (
                    <Typography variant="body1" fontWeight="bold">
                      {relatorio.condicao || 'N/A'}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Contratada
                  </Typography>
                  {editMode ? (
                    <TextField
                      value={editData.contratada || ''}
                      onChange={(e) => handleChange('contratada', e.target.value)}
                      size="small"
                      fullWidth
                      placeholder="Nome da contratada..."
                    />
                  ) : (
                    <Typography variant="body1">
                      {relatorio.contratada || 'N/A'}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Contrato
                  </Typography>
                  {editMode ? (
                    <TextField
                      value={editData.contrato_numero || ''}
                      onChange={(e) => handleChange('contrato_numero', e.target.value)}
                      size="small"
                      fullWidth
                      placeholder="Nº do contrato..."
                    />
                  ) : (
                    <Typography variant="body1">
                      {relatorio.contrato_numero || 'N/A'}
                    </Typography>
                  )}
                </Grid>
              </Grid>

              {/* Descrição do Serviço */}
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Descrição do Serviço
              </Typography>
              {editMode ? (
                <TextField
                  multiline
                  rows={4}
                  value={editData.descricao_servico || ''}
                  onChange={(e) => handleChange('descricao_servico', e.target.value)}
                  fullWidth
                  placeholder="Descreva o serviço realizado..."
                />
              ) : relatorio.descricao_servico ? (
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}
                >
                  {relatorio.descricao_servico}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma descrição informada
                </Typography>
              )}

              {/* Transcrição de Áudio */}
              {(relatorio.transcricao_audio || editMode) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <Mic color="primary" /> Transcrição de Áudio
                  </Typography>
                  {editMode ? (
                    <TextField
                      multiline
                      rows={3}
                      value={editData.transcricao_audio || ''}
                      onChange={(e) => handleChange('transcricao_audio', e.target.value)}
                      fullWidth
                      placeholder="Transcrição do áudio..."
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        bgcolor: 'info.lighter',
                        p: 2,
                        borderRadius: 1,
                      }}
                    >
                      {relatorio.transcricao_audio}
                    </Typography>
                  )}
                </>
              )}

              {/* Mão de Obra */}
              {(maoDeObra.length > 0 || editMode) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  {editMode ? (
                    <>
                      <Typography variant="h6" gutterBottom>
                        Mão de Obra
                      </Typography>
                      {editMaoDeObra.map((item, i) => (
                        <Stack key={i} direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
                          <TextField
                            label="Empresa/Empreiteira"
                            value={item.nome}
                            onChange={(e) => setEditMaoDeObra((prev) => prev.map((it, idx) => idx === i ? { ...it, nome: e.target.value } : it))}
                            size="small"
                            sx={{ flex: 2 }}
                          />
                          <TextField
                            label="Funcionários"
                            type="number"
                            inputProps={{ min: 1 }}
                            value={item.funcionarios}
                            onChange={(e) => setEditMaoDeObra((prev) => prev.map((it, idx) => idx === i ? { ...it, funcionarios: parseInt(e.target.value) || 1 } : it))}
                            size="small"
                            sx={{ flex: 1 }}
                          />
                          <IconButton size="small" color="error" onClick={() => setEditMaoDeObra((prev) => prev.filter((_, idx) => idx !== i))}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                      <Button size="small" startIcon={<AddIcon />} onClick={() => setEditMaoDeObra((prev) => [...prev, { nome: '', funcionarios: 1 }])}>
                        Adicionar Empresa
                      </Button>
                    </>
                  ) : (
                    (() => {
                      const formatoNovo = typeof maoDeObra[0] === 'object' && maoDeObra[0].nome;
                      const totalFuncionarios = formatoNovo
                        ? maoDeObra.reduce((acc, e) => acc + (parseInt(e.funcionarios) || 0), 0)
                        : maoDeObra.length;
                      return (
                        <>
                          <Typography variant="h6" gutterBottom>
                            Mão de Obra (Total: {totalFuncionarios} funcionários)
                          </Typography>
                          <List dense>
                            {formatoNovo
                              ? maoDeObra
                                  .filter((e) => e.funcionarios > 0)
                                  .map((empresa, i) => (
                                    <ListItem key={i}>
                                      <ListItemText
                                        primary={empresa.nome}
                                        secondary={`${empresa.funcionarios} funcionário(s)`}
                                      />
                                    </ListItem>
                                  ))
                              : maoDeObra.map((item, i) => (
                                  <ListItem key={i}>
                                    <ListItemText
                                      primary={typeof item === 'object' ? item.nome || item.descricao : item}
                                    />
                                  </ListItem>
                                ))}
                          </List>
                        </>
                      );
                    })()
                  )}
                </>
              )}

              {/* Equipamentos */}
              {(equipamentos.length > 0 || editMode) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Equipamentos {editMode ? '' : `(${equipamentos.length})`}
                  </Typography>
                  {editMode ? (
                    <>
                      {editEquipamentos.map((item, i) => (
                        <Stack key={i} direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
                          <TextField
                            value={item}
                            onChange={(e) => setEditEquipamentos((prev) => prev.map((it, idx) => idx === i ? e.target.value : it))}
                            size="small"
                            placeholder="Nome do equipamento..."
                            fullWidth
                          />
                          <IconButton size="small" color="error" onClick={() => setEditEquipamentos((prev) => prev.filter((_, idx) => idx !== i))}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                      <Button size="small" startIcon={<AddIcon />} onClick={() => setEditEquipamentos((prev) => [...prev, ''])}>
                        Adicionar Equipamento
                      </Button>
                    </>
                  ) : (
                    <List dense>
                      {equipamentos.map((item, i) => (
                        <ListItem key={i}>
                          <ListItemText primary={typeof item === 'object' ? item.nome || item.descricao : item} />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </>
              )}

              {/* Atividades */}
              {(atividades.length > 0 || editMode) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Atividades {editMode ? '' : `(${atividades.length})`}
                  </Typography>
                  {editMode ? (
                    <>
                      {editAtividades.map((item, i) => (
                        <Stack key={i} direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
                          <TextField
                            value={item}
                            onChange={(e) => setEditAtividades((prev) => prev.map((it, idx) => idx === i ? e.target.value : it))}
                            size="small"
                            placeholder="Descrição da atividade..."
                            fullWidth
                          />
                          <IconButton size="small" color="error" onClick={() => setEditAtividades((prev) => prev.filter((_, idx) => idx !== i))}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                      <Button size="small" startIcon={<AddIcon />} onClick={() => setEditAtividades((prev) => [...prev, ''])}>
                        Adicionar Atividade
                      </Button>
                    </>
                  ) : (
                    <List dense>
                      {atividades.map((item, i) => (
                        <ListItem key={i}>
                          <ListItemText primary={typeof item === 'object' ? item.nome || item.descricao : item} />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </>
              )}

              {/* Áudio */}
              {audioUrl && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <Mic color="primary" /> Áudio Gravado
                  </Typography>
                  <audio controls src={audioUrl} style={{ width: '100%' }} />
                </>
              )}

              {/* Fotos */}
              <>
                <Divider sx={{ my: 2 }} />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhotoCamera color="primary" /> Fotos ({fotosUrls.length})
                    {editMode && (
                      <Chip
                        label={`Máx: ${MAX_FOTOS} fotos / ${MAX_TAMANHO_FOTOS_MB}MB`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    )}
                  </Typography>
                  {editMode && (
                    <Tooltip title="Adicionar novas fotos (substituirá as atuais)">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => fotoInputRef.current?.click()}
                      >
                        Adicionar Fotos
                      </Button>
                    </Tooltip>
                  )}
                </Box>

                {/* Input de arquivo oculto */}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  ref={fotoInputRef}
                  style={{ display: 'none' }}
                  onChange={handleAddFotos}
                />

                {/* Fotos existentes */}
                {fotosUrls.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      {editMode ? 'Fotos atuais (serão substituídas ao adicionar novas):' : ''}
                    </Typography>
                    <Grid container spacing={1}>
                      {fotosUrls.map((url, index) => (
                        <Grid item xs={4} sm={3} key={index}>
                          <Card
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.8 },
                              border:
                                editMode && novasFotos.length > 0 ? '2px dashed orange' : 'none',
                              opacity: editMode && novasFotos.length > 0 ? 0.5 : 1,
                            }}
                            onClick={() => !editMode && setSelectedImage(url)}
                          >
                            <CardMedia
                              component="img"
                              height="80"
                              image={url}
                              alt={`Foto ${index + 1}`}
                              sx={{ objectFit: 'cover' }}
                            />
                          </Card>
                          {editMode ? (
                            <TextField
                              size="small"
                              fullWidth
                              sx={{ mt: 0.5 }}
                              label="Descrição"
                              value={editFotosDescricoes[index] || ''}
                              inputProps={{ maxLength: 100 }}
                              helperText={`${(editFotosDescricoes[index] || '').length}/100`}
                              onChange={(e) =>
                                setEditFotosDescricoes((prev) => {
                                  const arr = [...prev];
                                  arr[index] = e.target.value.slice(0, 100);
                                  return arr;
                                })
                              }
                            />
                          ) : (
                            (fotosDescricoes[index] || '').trim() && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                {fotosDescricoes[index]}
                              </Typography>
                            )
                          )}
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Novas fotos (preview) */}
                {editMode && novasFotos.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="success.main" gutterBottom>
                      Novas fotos ({novasFotos.length}) - {calcularTamanhoMB(novasFotos).toFixed(2)}
                      MB:
                    </Typography>
                    <Grid container spacing={1}>
                      {novasFotos.map((foto, index) => (
                        <Grid item xs={4} sm={3} key={index}>
                          <Card sx={{ position: 'relative' }}>
                            <CardMedia
                              component="img"
                              height="80"
                              image={foto.preview}
                              alt={foto.name}
                              sx={{ objectFit: 'cover' }}
                            />
                            <IconButton
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                bgcolor: 'rgba(255,255,255,0.8)',
                                '&:hover': { bgcolor: 'error.light', color: 'white' },
                              }}
                              onClick={() => handleRemoveFoto(index)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Card>
                          <TextField
                            size="small"
                            fullWidth
                            sx={{ mt: 0.5 }}
                            label="Descrição"
                            value={foto.descricao || ''}
                            inputProps={{ maxLength: 100 }}
                            helperText={`${(foto.descricao || '').length}/100`}
                            onChange={(e) =>
                              setNovasFotos((prev) =>
                                prev.map((f, i) =>
                                  i === index ? { ...f, descricao: e.target.value.slice(0, 100) } : f
                                )
                              )
                            }
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </>

              {/* Vídeos */}
              <>
                <Divider sx={{ my: 2 }} />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Videocam color="primary" /> Vídeos ({videosUrls.length})
                    {editMode && (
                      <Chip
                        label={`Máx: ${MAX_VIDEOS} vídeos / ${MAX_TAMANHO_VIDEOS_MB}MB`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    )}
                  </Typography>
                  {editMode && (
                    <Tooltip title="Adicionar novos vídeos (substituirá os atuais)">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => videoInputRef.current?.click()}
                      >
                        Adicionar Vídeos
                      </Button>
                    </Tooltip>
                  )}
                </Box>

                {/* Input de arquivo oculto */}
                <input
                  type="file"
                  accept="video/*"
                  multiple
                  ref={videoInputRef}
                  style={{ display: 'none' }}
                  onChange={handleAddVideos}
                />

                {/* Vídeos existentes */}
                {videosUrls.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      {editMode ? 'Vídeos atuais (serão substituídos ao adicionar novos):' : ''}
                    </Typography>
                    <Stack spacing={1}>
                      {videosUrls.map((url, index) => (
                        <Box
                          key={index}
                          sx={{
                            border:
                              editMode && novosVideos.length > 0 ? '2px dashed orange' : 'none',
                            opacity: editMode && novosVideos.length > 0 ? 0.5 : 1,
                            borderRadius: 1,
                          }}
                        >
                          <video src={url} controls style={{ width: '100%', maxHeight: 200 }} />
                          {editMode ? (
                            <TextField
                              size="small"
                              fullWidth
                              sx={{ mt: 0.5 }}
                              label="Descrição"
                              value={editVideosDescricoes[index] || ''}
                              inputProps={{ maxLength: 100 }}
                              helperText={`${(editVideosDescricoes[index] || '').length}/100`}
                              onChange={(e) =>
                                setEditVideosDescricoes((prev) => {
                                  const arr = [...prev];
                                  arr[index] = e.target.value.slice(0, 100);
                                  return arr;
                                })
                              }
                            />
                          ) : (
                            (videosDescricoes[index] || '').trim() && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                {videosDescricoes[index]}
                              </Typography>
                            )
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Novos vídeos (preview) */}
                {editMode && novosVideos.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="success.main" gutterBottom>
                      Novos vídeos ({novosVideos.length}) -{' '}
                      {calcularTamanhoMB(novosVideos).toFixed(2)}MB:
                    </Typography>
                    <Stack spacing={1}>
                      {novosVideos.map((video, index) => (
                        <Box key={index} sx={{ position: 'relative' }}>
                          <video
                            src={video.preview}
                            controls
                            style={{ width: '100%', maxHeight: 200 }}
                          />
                          <IconButton
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'rgba(255,255,255,0.8)',
                              '&:hover': { bgcolor: 'error.light', color: 'white' },
                            }}
                            onClick={() => handleRemoveVideo(index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                            {video.name}
                          </Typography>
                          <TextField
                            size="small"
                            fullWidth
                            sx={{ mt: 0.5 }}
                            label="Descrição"
                            value={video.descricao || ''}
                            inputProps={{ maxLength: 100 }}
                            helperText={`${(video.descricao || '').length}/100`}
                            onChange={(e) =>
                              setNovosVideos((prev) =>
                                prev.map((v, i) =>
                                  i === index
                                    ? { ...v, descricao: e.target.value.slice(0, 100) }
                                    : v
                                )
                              )
                            }
                          />
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </>

              {/* Histórico de Mídias */}
              {historicoMidias.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HistoryIcon color="secondary" /> Histórico de Mídias (
                      {historicoMidias.length})
                    </Typography>
                    <Button size="small" onClick={() => setShowHistorico(!showHistorico)}>
                      {showHistorico ? 'Ocultar' : 'Mostrar'}
                    </Button>
                  </Box>
                  {showHistorico && (
                    <Box sx={{ mt: 2 }}>
                      <Grid container spacing={1}>
                        {historicoMidias.map((item, index) => (
                          <Grid item xs={6} sm={4} key={index}>
                            <Card sx={{ p: 1, bgcolor: 'grey.100' }}>
                              {item.tipo_midia === 'foto' ? (
                                <CardMedia
                                  component="img"
                                  height="60"
                                  image={item.arquivo_url}
                                  alt={`Histórico ${index + 1}`}
                                  sx={{ objectFit: 'cover', borderRadius: 1 }}
                                  onClick={() => setSelectedImage(item.arquivo_url)}
                                  style={{ cursor: 'pointer' }}
                                />
                              ) : (
                                <video
                                  src={item.arquivo_url}
                                  controls
                                  style={{ width: '100%', maxHeight: 60 }}
                                />
                              )}
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                {item.tipo_midia === 'foto' ? '📷' : '🎬'}{' '}
                                {new Date(item.data_alteracao).toLocaleDateString('pt-BR')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Por: {item.usuario}
                              </Typography>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </>
              )}

              {/* Documentos */}
              {docsUrls.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <Description color="primary" /> Documentos ({docsUrls.length})
                  </Typography>
                  <List dense>
                    {docsUrls.map((url, index) => (
                      <ListItem key={index} sx={{ display: 'block' }}>
                        <Box
                          component="a"
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          <ListItemIcon>
                            <Description />
                          </ListItemIcon>
                          <ListItemText
                            primary={`Documento ${index + 1}`}
                            secondary={
                              (editMode ? editDocsDescricoes[index] : docsDescricoes[index])
                                ? `${editMode ? editDocsDescricoes[index] : docsDescricoes[index]} | Clique para abrir`
                                : 'Clique para abrir'
                            }
                          />
                        </Box>
                        {editMode && (
                          <TextField
                            size="small"
                            fullWidth
                            sx={{ mt: 1 }}
                            label="Descrição"
                            value={editDocsDescricoes[index] || ''}
                            inputProps={{ maxLength: 100 }}
                            helperText={`${(editDocsDescricoes[index] || '').length}/100`}
                            onChange={(e) =>
                              setEditDocsDescricoes((prev) => {
                                const arr = [...prev];
                                arr[index] = e.target.value.slice(0, 100);
                                return arr;
                              })
                            }
                          />
                        )}
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {canUpdate && !editMode && (
            <Button
              startIcon={<Edit />}
              onClick={handleEditToggle}
              variant="contained"
              color="secondary"
            >
              Editar
            </Button>
          )}
          {editMode && (
            <>
              <Button
                startIcon={<Cancel />}
                onClick={handleEditToggle}
                variant="outlined"
                color="inherit"
              >
                Cancelar
              </Button>
              <Button
                startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                onClick={handleSave}
                variant="contained"
                color="primary"
                disabled={saving}
              >
                Salvar
              </Button>
            </>
          )}
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de visualização de imagem */}
      <Dialog open={!!selectedImage} onClose={() => setSelectedImage(null)} maxWidth="lg">
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={() => setSelectedImage(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Visualização"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          )}
        </Box>
      </Dialog>

      {/* Snackbar de feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
